import {gql} from '@apollo/client';
import {naturalSort} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import {DocumentNode} from 'graphql';
import React from 'react';

import * as analytics from 'analytics/Analytics';
import {pluralize} from 'BenchlingUtil';
import {IDENTITY_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import {PolicyTemplateNode} from 'graphql-client/__generated/graphqlGlobalTypes';
import {Pagination} from 'lib/Listings';
import ListingSettingsDropdown from 'nav/ListingSettingsDropdown';
import {smallSpacingBottom, miniSpacingLeft, smallSpacingTop, smallSpacingRight} from 'styles/Utilities';
import ExpandedClickableIcon from 'tenants/admin/ExpandedClickableIcon';
import OverflowDropdown from 'tenants/admin/OverflowDropdown';
import {GRAY, GRAY_DARKER, WHITE} from 'ui-library/Colors';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import BenchlingIcon, {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import Menu from 'ui-library/menus/Menu';
import Option from 'ui-library/menus/Option';
import ResultsPager from 'ui-library/ResultsPager';
import runModal from 'ui-library/runModal';
import {Cell, Column, Table, CellRenderOptions} from 'ui-library/simple-table/SimpleTable';
import {capitalize} from 'util/NameUtil';

import {PolicyTablePartyPoliceableListing} from './__generated/PolicyTablePartyPoliceableListing';
import EffectivePermissionsModal from './EffectivePermissionsModal';
import {OrganizationPermissionRole, TeamPermissionRole} from './OrganizationConstants';
import {AVAILABLE_LIMITS} from './usePoliceablePolicyTableLimitOffset';

type AccessPolicy =
  PolicyTablePartyPoliceableListing['partyPoliceables'][number]['collaborations'][number]['policy'];
type PoliceableCollaborationInfo = PolicyTablePartyPoliceableListing['partyPoliceables'][number];
type Collaboration = PolicyTablePartyPoliceableListing['partyPoliceables'][number]['collaborations'][number];
type Policeable = PolicyTablePartyPoliceableListing['partyPoliceables'][number]['policeable'];

export {PoliceableCollaborationInfo, AccessPolicy};

export const POLICY_TABLE_PARTY_POLICEABLE_LISTING_FRAGMENT: DocumentNode = gql`
  # collaborations does not query id
  # eslint-disable benchlint/id-field-required
  fragment PolicyTablePartyPoliceableListing on PartyPoliceablesListingNode {
    total
    partyPoliceables {
      party {
        ... on PartyInterface {
          id
        }
      }
      policeable {
        ... on PoliceableInterface {
          id
          name
        }
        ... on SourceInterface {
          id
          url
          ownerJson
          permissions {
            readable
            writable
            appendable
            admin
          }
        }
      }
      effectivePolicyStatements {
        itemType
        condition
        action
      }
      collaborations {
        teamRole
        organizationRole
        policy {
          ... on PolicyInterface {
            id
            name
            policyStatements {
              itemType
              condition
              action
            }
          }
        }
        party {
          ... on PartyInterface {
            id
            name
          }
        }
      }
    }
  }
`;

export {Policeable};

export interface PoliceablePolicyTablePropsLoading {
  isLoading: true;
  setOffset: (offset: number) => void;
  setLimit: (offset: number) => void;
  pagination: Pagination;
  resourceType: string;
  principalType: string;
}

export interface PoliceablePolicyTablePropsLoaded {
  isLoading: false;
  setOffset: (offset: number) => void;
  setLimit: (offset: number) => void;
  pagination: Pagination;
  resourceType: string;
  icon: BenchlingIconName;
  policyTemplate: PolicyTemplateNode;
  openPoliceablePage: (policeable: Policeable) => void;
  openPoliceableSettingsPage: (policeable: Policeable) => void;
  partyPoliceablesListing: PolicyTablePartyPoliceableListing;
  getPoliceableDisplayName?: (policeable: Policeable) => string;
  principalType: string;
}

export type PoliceablePolicyTableProps = PoliceablePolicyTablePropsLoaded | PoliceablePolicyTablePropsLoading;

function renderName(icon: BenchlingIconName, name: string): JSX.Element {
  return (
    <Cell>
      <div className={css(styles.nameCellChildren, styles.text)}>
        <BenchlingIcon className={css(styles.resourceTypeIcon)} icon={icon} />
        {name}
      </div>
    </Cell>
  );
}

function renderPolicies(policyNameToCollaborations: {
  [key: string]: {policy: AccessPolicy; collaborations: Array<Collaboration>};
}): JSX.Element {
  const policyNames = naturalSort(Object.keys(policyNameToCollaborations));
  return (
    <Cell className={css(styles.cellText, styles.text)}>
      {policyNames.map((policyName: string) => (
        <div key={policyName} className={css(styles.collaborationDiv)}>
          {policyName}
        </div>
      ))}
    </Cell>
  );
}

function renderAssignedTo(policyNameToCollaborations: {
  [key: string]: {policy: AccessPolicy; collaborations: Array<Collaboration>};
}): JSX.Element {
  const policyNames = naturalSort(Object.keys(policyNameToCollaborations));
  return (
    <Cell className={css(styles.cellText, styles.text)}>
      {policyNames.map((policyName: string) => (
        <div key={policyName} className={css(styles.collaborationDiv)}>
          {policyNameToCollaborations[policyName].collaborations
            .sort((obj1, obj2) => {
              const partyNameOrder = obj1.party.name.localeCompare(obj2.party.name, undefined, {
                numeric: true,
              });
              if (partyNameOrder) {
                return partyNameOrder;
              }
              // Sort admin roles after member roles
              if (obj1.teamRole != null && obj2.teamRole != null) {
                return obj2.teamRole - obj1.teamRole;
              }
              if (obj1.organizationRole != null && obj2.organizationRole != null) {
                return obj2.organizationRole - obj1.organizationRole;
              }
              return 0;
            })
            .map((collaboration) => {
              if (
                collaboration.organizationRole === OrganizationPermissionRole.ADMIN ||
                collaboration.teamRole === TeamPermissionRole.ADMIN
              ) {
                return `${collaboration.party.name} (Admin)`;
              }
              return collaboration.party.name;
            })
            .join(', ')}
        </div>
      ))}
    </Cell>
  );
}

interface PoliceableDropdownProps {
  resourceType: string;
  policeable: Policeable;
  openEffectivePermissionsModal: () => void;
  openPoliceablePage: (policeable: Policeable) => void;
  openPoliceableSettingsPage: (policeable: Policeable) => void;
}

function PoliceableDropdown(props: PoliceableDropdownProps): JSX.Element {
  const [isOpen, setIsOpen] = React.useState(false);
  return (
    <OverflowDropdown
      isOpen={isOpen}
      onToggle={(isOpen: boolean) => {
        if (isOpen) {
          analytics.trackEvent(IDENTITY_ANALYTICS_ACTIONS.OPENED_DATA_ACCESS_ROW_OVERFLOW_MENU, {
            objectType: props.resourceType,
            objectId: props.policeable.id,
          });
        }
        setIsOpen(isOpen);
      }}
      menu={
        <Menu width={Menu.WIDTHS.LARGE} height={Menu.HEIGHTS.MEDIUM}>
          <Option onSelect={props.openEffectivePermissionsModal}>View effective permissions</Option>
          <Option
            onSelect={() => {
              props.openPoliceablePage(props.policeable);
            }}
          >
            Navigate to {props.resourceType.toLowerCase()}
          </Option>
          <Option
            onSelect={() => {
              props.openPoliceableSettingsPage(props.policeable);
            }}
          >
            Edit {props.resourceType.toLowerCase()} settings
          </Option>
        </Menu>
      }
      icon={<ExpandedClickableIcon icon={<BenchlingIcon icon="overflow" />} />}
    />
  );
}

function renderOverflowButtonCell({
  resourceType,
  policeable,
  openEffectivePermissionsModal,
  openPoliceablePage,
  openPoliceableSettingsPage,
}: {
  resourceType: string;
  policeable: Policeable;
  openEffectivePermissionsModal: () => void;
  openPoliceablePage: (policeable: Policeable) => void;
  openPoliceableSettingsPage: (policeable: Policeable) => void;
}): JSX.Element {
  return (
    <Cell className={css(styles.overflowButtonCell)}>
      <PoliceableDropdown
        resourceType={resourceType}
        policeable={policeable}
        openEffectivePermissionsModal={openEffectivePermissionsModal}
        openPoliceablePage={openPoliceablePage}
        openPoliceableSettingsPage={openPoliceableSettingsPage}
      />
    </Cell>
  );
}

function TableOrLoadingContainer(props: PoliceablePolicyTableProps): JSX.Element {
  if (props.isLoading) {
    return (
      <div className={css(styles.tableOrLoadingContainer)}>
        <CenteredHorizontalThrobber />
      </div>
    );
  }
  // create a map of policy -> collaboration for each row to dedupe collaborations
  const collaborationPolicyMappings = props.partyPoliceablesListing.partyPoliceables.map(
    (policeableCollaborationInfo: PoliceableCollaborationInfo) =>
      policeableCollaborationInfo.collaborations.reduce(
        (
          policyToPartyMap: {[key: string]: {policy: AccessPolicy; collaborations: Array<Collaboration>}},
          currentCollaboration: Collaboration
        ) => {
          if (policyToPartyMap[currentCollaboration.policy.name]) {
            policyToPartyMap[currentCollaboration.policy.name].collaborations.push(currentCollaboration);
          } else {
            policyToPartyMap[currentCollaboration.policy.name] = {
              collaborations: [currentCollaboration],
              policy: currentCollaboration.policy,
            };
          }
          return policyToPartyMap;
        },
        {}
      )
  );

  const getName = props.getPoliceableDisplayName ?? ((policeable: Policeable): string => policeable.name);

  const policies: Array<Array<AccessPolicy>> = Object.values(collaborationPolicyMappings).map(
    (policyNameToCollaborations: {
      [key: string]: {policy: AccessPolicy; collaborations: Array<Collaboration>};
    }) => Object.values(policyNameToCollaborations).map((pc) => pc.policy)
  );
  const openEffectivePermissionsModal = policies.map((p, i) => async () => {
    const partyPoliceable = props.partyPoliceablesListing.partyPoliceables[i];
    await runModal(EffectivePermissionsModal, {
      resource: {
        name: getName(partyPoliceable.policeable),
        type: props.resourceType,
        id: partyPoliceable.policeable.id,
      },
      policyTemplate: props.policyTemplate,
      policies: p,
      effectivePolicyStatements: partyPoliceable.effectivePolicyStatements,
      principalType: props.principalType,
    });
  });
  return (
    <Table
      className={classNames(css(styles.table), css(styles.tableOrLoadingContainer))}
      numRows={props.partyPoliceablesListing.partyPoliceables.length}
      emptyTableText={`No ${pluralize(2, props.resourceType)} for ${props.principalType}.`}
      onRowClick={(i: number) => {
        openEffectivePermissionsModal[i]();
      }}
    >
      <Column
        header={capitalize(props.resourceType)}
        renderCell={(i: number) =>
          renderName(props.icon, getName(props.partyPoliceablesListing.partyPoliceables[i].policeable))
        }
      />
      <Column
        header="Access Policies"
        renderCell={(i: number) => renderPolicies(collaborationPolicyMappings[i])}
      />
      <Column
        header="Assigned To"
        renderCell={(i: number) => renderAssignedTo(collaborationPolicyMappings[i])}
      />
      <Column
        header={<Cell className={css(styles.overflowButtonCell)} />}
        renderCell={(i: number, options: CellRenderOptions) =>
          renderOverflowButtonCell({
            resourceType: props.resourceType,
            policeable: props.partyPoliceablesListing.partyPoliceables[i].policeable,
            openEffectivePermissionsModal: openEffectivePermissionsModal[i],
            openPoliceablePage: props.openPoliceablePage,
            openPoliceableSettingsPage: props.openPoliceableSettingsPage,
          })
        }
      />
    </Table>
  );
}

export default function PoliceablePolicyTable(props: PoliceablePolicyTableProps): JSX.Element {
  return (
    <>
      <div className={css(styles.resultPagerContainer)}>
        <div className={css(styles.resultPagerContainerContent)}>
          <ResultsPager
            allowFastForwardAndBackward={true}
            setOffset={props.setOffset}
            isLoading={props.isLoading}
            pagination={props.pagination}
          />
        </div>
        <div className={css(styles.resultPagerContainerContent)}>
          <ListingSettingsDropdown
            pageSizeConfig={{
              availablePageSizes: AVAILABLE_LIMITS,
              pageSize: props.pagination.limit,
              onSetPageSize: props.setLimit,
            }}
            iconAltText="Results Per Page"
          />
        </div>
      </div>
      <TableOrLoadingContainer {...props} />
    </>
  );
}

const styles = StyleSheet.create({
  cellText: {
    color: GRAY_DARKER,
  },
  collaborationDiv: {
    minHeight: '16px',
    ':not(:last-child)': {
      ...smallSpacingBottom,
    },
  },
  resultPagerContainer: {...miniSpacingLeft, ...smallSpacingTop},
  resultPagerContainerContent: {
    display: 'inline-block',
    verticalAlign: 'top',
  },
  tableOrLoadingContainer: {
    ...smallSpacingTop,
    maxWidth: '1000px',
  },
  table: {
    backgroundColor: WHITE,
  },
  text: {
    fontSize: '13px',
    maxWidth: '1000px',
  },
  resourceTypeIcon: {
    ...smallSpacingRight,
    color: GRAY,
  },
  nameCellChildren: {
    alignItems: 'center',
    display: 'flex',
  },
  overflowButtonCell: {
    width: 20,
    color: GRAY_DARKER,
  },
});
